if (!customElements.get('mc-card-diopter')) {
  customElements.define(
    'mc-card-diopter',
    class McCardDiopter extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector('input[name="id"]');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.submitText = this.submitButton?.querySelector('span');
        this.baseSelects = Array.from(this.querySelectorAll('[data-mc-card-option]'));
        this.bundleSelects = Array.from(this.querySelectorAll('[data-mc-bundle-option]'));
        this.baseFields = this.querySelector('[data-base-fields]');
        this.bundleFields = this.querySelector('[data-bundle-fields]');
        this.bundleMode = false;
        this.abortController = null;
        this.requestNumber = 0;

        if (!this.form || !this.variantInput || !this.submitButton || !this.baseSelects.length) return;

        this.baseSelects.concat(this.bundleSelects).forEach((select) => {
          select.addEventListener('change', () => this.resolveVariant());
        });
        this.addEventListener('click', (event) => {
          const packButton = event.target.closest('[data-pack-mode]');
          if (!packButton) return;
          this.setPackMode(packButton.dataset.packMode === 'bundle');
        });
        this.setPackMode(false);
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      async resolveVariant() {
        this.clearError();
        const activeSelects = this.bundleMode ? this.bundleSelects : this.baseSelects;
        if (!activeSelects.length || activeSelects.some((select) => !select.value)) {
          this.setState('choose');
          return;
        }

        const optionValueIds = this.getOptionValueIds(activeSelects);
        if (optionValueIds.some((id) => !id)) {
          this.setError('Please choose a valid power.');
          this.setState('unavailable');
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        const currentRequest = ++this.requestNumber;
        this.setState('loading');

        try {
          const productUrl = this.bundleMode ? this.dataset.bundleUrl : this.dataset.productUrl;
          const variantId = await this.resolveOptionValues(productUrl, optionValueIds);
          if (currentRequest !== this.requestNumber) return;

          this.variantInput.value = variantId;
          this.variantInput.disabled = false;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.setState('ready');
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.setError('Could not check this power. Please try again.');
          this.setState('unavailable');
        }
      }

      getOptionValueIds(selects) {
        return selects.map((select) => select.selectedOptions[0]?.dataset.optionValueId);
      }

      async resolveOptionValues(productUrl, optionValueIds) {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('option_values', optionValueIds.join(','));
        const response = await fetch(url.toString(), {
          signal: this.abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Variant request failed: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const productInfo = html.querySelector('product-info');
        const resolvedInput = productInfo?.querySelector('input.product-variant-id[name="id"]');
        const resolvedButton = productInfo?.querySelector('[id^="ProductSubmitButton-"]');
        const variantId = resolvedInput?.value;
        if (!variantId || resolvedInput.disabled || resolvedButton?.disabled) {
          throw new Error('Selected prescription is sold out.');
        }
        return variantId;
      }

      setPackMode(bundleMode) {
        if (bundleMode && (!this.dataset.bundleUrl || !this.bundleSelects.length)) return;
        this.bundleMode = bundleMode;
        this.classList.toggle('is-bundle-mode', bundleMode);
        if (this.baseFields) this.baseFields.hidden = bundleMode;
        if (this.bundleFields) this.bundleFields.hidden = !bundleMode;

        const inactiveSelects = bundleMode ? this.baseSelects : this.bundleSelects;
        inactiveSelects.forEach((select) => { select.value = ''; });

        this.querySelectorAll('[data-pack-mode]').forEach((button) => {
          const active = button.dataset.packMode === (bundleMode ? 'bundle' : 'single');
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });

        const quantityInput = this.form.querySelector('input[name="quantity"]');
        if (quantityInput) quantityInput.value = '1';

        const dialog = this.closest('dialog');
        const price = dialog?.querySelector('[data-quick-add-price]');
        const meta = dialog?.querySelector('[data-quick-add-meta]');
        const title = dialog?.querySelector('[data-quick-add-title]');
        const baseMedia = dialog?.querySelector('[data-quick-add-media-base]');
        const bundleMedia = dialog?.querySelector('[data-quick-add-media-bundle]');
        const baseDescription = dialog?.querySelector('[data-quick-add-description-base]');
        const bundleDescription = dialog?.querySelector('[data-quick-add-description-bundle]');
        const baseDetails = dialog?.querySelector('[data-quick-add-details-base]');
        const bundleDetails = dialog?.querySelector('[data-quick-add-details-bundle]');
        const fullLink = dialog?.querySelector('[data-quick-add-full-link]');
        if (price) price.textContent = bundleMode ? this.dataset.bundlePrice : this.dataset.basePrice;
        if (meta) meta.textContent = bundleMode ? this.dataset.bundleLabel : this.dataset.baseLabel;
        if (title) title.textContent = bundleMode ? this.dataset.bundleTitle : this.dataset.baseTitle;
        if (baseMedia) baseMedia.hidden = bundleMode;
        if (bundleMedia) bundleMedia.hidden = !bundleMode;
        if (baseDescription) baseDescription.hidden = bundleMode;
        if (bundleDescription) bundleDescription.hidden = !bundleMode;
        if (baseDetails) baseDetails.hidden = bundleMode;
        if (bundleDetails) bundleDetails.hidden = !bundleMode;
        if (fullLink) fullLink.href = bundleMode ? this.dataset.bundleUrl : this.dataset.productUrl;

        this.setState('choose');
      }

      setState(state) {
        const labels = {
          choose: 'Choose powers',
          loading: 'Checking…',
          ready: 'Add to cart',
          soldout: 'Sold out',
          unavailable: 'Unavailable',
        };
        const ready = state === 'ready';
        this.dataset.state = state;
        this.variantInput.disabled = !ready;
        if (!ready) this.variantInput.value = '';
        this.submitButton.disabled = !ready;
        this.submitButton.setAttribute('aria-disabled', String(!ready));
        if (this.submitText) this.submitText.textContent = labels[state];
      }

      setError(message) {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const error = wrapper?.querySelector('.product-form__error-message');
        if (!wrapper || !error) return;
        error.textContent = message;
        wrapper.hidden = false;
      }

      clearError() {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        if (wrapper) wrapper.hidden = true;
      }
    }
  );
}
